import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useMemo } from 'react';
import { request } from '../api';
import { useTranslation } from 'react-i18next';
import { GetRequestResponse } from '@/shared/types/requests.types';
import { useToastNotif } from '@/shared/hooks/useToastNotif/useToastNotif';

type QueryFetchProps<T> = {
  url: string;
  params?: Record<string, string | null | undefined | number>; // Use `any` or a more specific type if possible
  initialData?: T;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  keepPrevious?: boolean;
  retry?: boolean | number;
  showNotification?: boolean;
};

export const useFetchQueries = <T>({
  url,
  params = {},
  initialData,
  enabled = true,
  refetchOnWindowFocus = true,
  keepPrevious,
  retry = 3,
  showNotification = true,
}: QueryFetchProps<T>) => {
  const { showToast } = useToastNotif();
  const stringifiedParams = Object.entries(params).reduce(
    (acc, [key, value]) => `${acc}-${key}-${value}`,
    '',
  );
  const computedQueryKey = `${url}-${stringifiedParams}`;
  const cachedQueryKey = useMemo(() => [computedQueryKey], [computedQueryKey]);
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: cachedQueryKey,
    queryFn: async (): Promise<T> => {
      const { data } = await request.get<T>(url, { params });
      return data;
    },
    // @ts-expect-error - fix later
    placeholderData: keepPrevious ? keepPreviousData : initialData,
    enabled,
    refetchOnWindowFocus,
    retry,
  });

  useEffect(() => {
    if (query.error) {
      const message = t('Что-то пошло не так');
      let errorMessage = '';
      if (
        axios.isAxiosError<GetRequestResponse<T>, Record<string, string>>(
          query.error,
        )
      ) {
        errorMessage = query?.error?.response?.data?.message as string;
      }
      if (showNotification) {
        showToast({
          message: errorMessage || message,
          type: 'error',
        });
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.error, showToast]);

  return query;
};
